import { useCallback, useState } from "react";
import {
  DEFAULT_PLAN_LIST_COLUMNS,
  type PlanListColumn,
  parsePlanListColumns,
  togglePlanListColumn,
} from "@/lib/plan-list-columns";

const STORAGE_KEY = "nos.plans.columns";

function readStorage(): PlanListColumn[] | null {
  try {
    return parsePlanListColumns(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStorage(columns: readonly PlanListColumn[] | null): void {
  try {
    if (columns === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // localStorage unavailable (private mode) — the choice lasts for this visit only.
  }
}

/** The Resource Plans list's visible columns, remembered per browser. */
export function usePlanListColumns(): {
  columns: PlanListColumn[];
  toggle: (column: PlanListColumn) => void;
  reset: () => void;
} {
  const [columns, setColumns] = useState<PlanListColumn[]>(
    () => readStorage() ?? [...DEFAULT_PLAN_LIST_COLUMNS],
  );

  const toggle = useCallback(
    (column: PlanListColumn) => {
      const next = togglePlanListColumn(columns, column);
      writeStorage(next);
      setColumns(next);
    },
    [columns],
  );

  const reset = useCallback(() => {
    writeStorage(null);
    setColumns([...DEFAULT_PLAN_LIST_COLUMNS]);
  }, []);

  return { columns, toggle, reset };
}
